/**
 * NetworkSchemas - Type-safe message payloads with runtime validation
 * 
 * Provides TypeScript interfaces and type guards for all network messages.
 * In development mode, invalid payloads trigger console warnings.
 */

import type { PlayerState, LobbyPlayer } from './NetworkManager';

// ============================================================================
// Message Type Constants
// ============================================================================

export const MessageType = {
    // Player sync
    PLAYER_POSITION: 'PLAYER_POSITION',
    REMOTE_STATES_SYNC: 'REMOTE_STATES_SYNC',

    // Fart actions
    CHARGE_START: 'CHARGE_START',
    CHARGED_FART: 'CHARGED_FART',
    FART_CANCEL: 'FART_CANCEL',

    // Game events
    VICTORY: 'victory',
    PICKUP_COLLECTED: 'PICKUP_COLLECTED',

    // Lobby
    LOBBY_PLAYER_LIST: 'LOBBY_PLAYER_LIST',
    START_GAME: 'START_GAME',

    // Session (from SessionMessages in NetworkManager)
    SESSION_ROUND_START: 'SESSION_ROUND_START',
    SESSION_ROUND_END: 'SESSION_ROUND_END',
    SESSION_PLAYER_READY: 'SESSION_PLAYER_READY',
    SESSION_PLAYER_LEFT: 'SESSION_PLAYER_LEFT',
} as const;

export type MessageTypeKey = keyof typeof MessageType;
export type MessageTypeValue = typeof MessageType[MessageTypeKey];

// ============================================================================
// Payload Interfaces
// ============================================================================

/** PLAYER_POSITION payload - full player state */
export type PlayerPositionPayload = PlayerState;

/** REMOTE_STATES_SYNC payload - array of player states (host relay) */
export type RemoteStatesSyncPayload = PlayerState[];

/** CHARGE_START / FART_CANCEL payload */
export interface PlayerIdPayload {
    playerId: string;
}

/** CHARGED_FART payload */
export interface ChargedFartPayload {
    x: number;
    y: number;
    chargePercent: number;
    ownerId: string;
    fartType: string;
}

/** victory payload */
export interface VictoryPayload {
    winnerId: string;
}

/** PICKUP_COLLECTED payload */
export interface PickupCollectedPayload {
    playerId: string;
    pickupId: string;
}

/** LOBBY_PLAYER_LIST payload */
export type LobbyPlayerListPayload = LobbyPlayer[];

/** START_GAME payload */
export interface StartGamePayload {
    seed: number;
    width: number;
    height: number;
}

/** SESSION_ROUND_START payload */
export interface RoundStartPayload {
    seed: number;
}

// ============================================================================
// Type Guards (Runtime Validators)
// ============================================================================

function isObject(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object';
}

function isString(v: unknown): v is string {
    return typeof v === 'string';
}

function isNumber(v: unknown): v is number {
    return typeof v === 'number' && !isNaN(v);
}

/** Validate PlayerState-like object */
export function isPlayerState(p: unknown): p is PlayerState {
    if (!isObject(p)) return false;
    return isString(p.id) && p.id.length > 0
        && isNumber(p.x) && isNumber(p.y)
        && isNumber(p.health);
}

/** Validate player position payload */
export function isPlayerPositionPayload(p: unknown): p is PlayerPositionPayload {
    return isPlayerState(p);
}

/** Validate remote states sync payload */
export function isRemoteStatesSyncPayload(p: unknown): p is RemoteStatesSyncPayload {
    return Array.isArray(p) && p.every(isPlayerState);
}

/** Validate playerId-only payloads */
export function isPlayerIdPayload(p: unknown): p is PlayerIdPayload {
    return isObject(p) && isString(p.playerId);
}

/** Validate charged fart payload */
export function isChargedFartPayload(p: unknown): p is ChargedFartPayload {
    if (!isObject(p)) return false;
    return isNumber(p.x) && isNumber(p.y)
        && isNumber(p.chargePercent)
        && isString(p.ownerId)
        && isString(p.fartType);
}

/** Validate victory payload */
export function isVictoryPayload(p: unknown): p is VictoryPayload {
    return isObject(p) && isString(p.winnerId);
}

/** Validate pickup collected payload */
export function isPickupCollectedPayload(p: unknown): p is PickupCollectedPayload {
    return isObject(p) && isString(p.playerId) && isString(p.pickupId);
}

/** Validate lobby player list payload */
export function isLobbyPlayerListPayload(p: unknown): p is LobbyPlayerListPayload {
    if (!Array.isArray(p)) return false;
    return p.every(item =>
        isObject(item)
        && isString(item.id)
        && isString(item.name)
        && typeof item.isReady === 'boolean'
        && typeof item.isHost === 'boolean'
    );
}

/** Validate start game payload */
export function isStartGamePayload(p: unknown): p is StartGamePayload {
    return isObject(p) && isNumber(p.seed) && isNumber(p.width) && isNumber(p.height);
}

/** Validate round start payload */
export function isRoundStartPayload(p: unknown): p is RoundStartPayload {
    return isObject(p) && isNumber(p.seed);
}

// ============================================================================
// Validator Registry
// ============================================================================

type ValidatorFn = (payload: unknown) => boolean;

export const validators: Partial<Record<string, ValidatorFn>> = {
    [MessageType.PLAYER_POSITION]: isPlayerPositionPayload,
    [MessageType.REMOTE_STATES_SYNC]: isRemoteStatesSyncPayload,
    [MessageType.CHARGE_START]: isPlayerIdPayload,
    [MessageType.CHARGED_FART]: isChargedFartPayload,
    [MessageType.FART_CANCEL]: isPlayerIdPayload,
    [MessageType.VICTORY]: isVictoryPayload,
    [MessageType.PICKUP_COLLECTED]: isPickupCollectedPayload,
    [MessageType.LOBBY_PLAYER_LIST]: isLobbyPlayerListPayload,
    [MessageType.START_GAME]: isStartGamePayload,
    [MessageType.SESSION_PLAYER_READY]: isPlayerIdPayload,
    [MessageType.SESSION_ROUND_START]: isRoundStartPayload,
    [MessageType.SESSION_PLAYER_LEFT]: isPlayerIdPayload,
};

/**
 * Validate a message payload against its schema.
 * Returns true if valid or no validator exists for this type.
 * In dev mode, logs a warning for invalid payloads.
 */
export function validatePayload(type: string, payload: unknown): boolean {
    const validator = validators[type];
    if (!validator) {
        // No validator for this type - allow it through
        return true;
    }

    const isValid = validator(payload);

    if (!isValid && import.meta.env.DEV) {
        console.warn(`[NetworkSchemas] Invalid ${type} payload:`, payload);
    }

    return isValid;
}
